"use client";

import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link } from '@/components/common/Link';
import { Button } from '@/components/ui/button';
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink, navigationMenuTriggerStyle } from '@/components/ui/navigation-menu';
import { MenuProvider, useMenu } from '@/integrations/wordpress/WordPressMenuProvider';
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="8861" className="flex flex-col gap-6 w-full items-center">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-6 w-32 bg-muted/20 animate-pulse rounded-none" />)}
      </div>;
  }
  const itemsToRender = menuItems.length > 0 ? menuItems : [{
    id: 'menu',
    label: 'THE MENU',
    href: '/TheMenuPage'
  }, {
    id: 'kitchen',
    label: 'THE KITCHEN',
    href: '/TheKitchenPage'
  }];
  return <nav className="flex flex-col gap-6 items-center justify-center w-full">
      {itemsToRender.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-xs font-mono uppercase tracking-[0.2em] text-foreground hover:text-primary transition-colors duration-300" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 left-0 right-0 z-50 w-full transition-all duration-500 dark ${isScrolled ? 'bg-background/80 backdrop-blur-md border-b border-border py-2' : 'bg-background border-b border-transparent py-4'}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/NordicBitePage" className="block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602682/img/affluent_clients_logo.png" alt="NordicBite" className="h-10 w-auto object-contain filter brightness-0 invert" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <NavigationMenu>
            <NavigationMenuList className="gap-12">
              <NavigationMenuItem>
                <NavigationMenuLink asChild className="bg-transparent hover:bg-transparent focus:bg-transparent data-[active]:bg-transparent data-[state=open]:bg-transparent">
                  <Link to="/TheMenuPage" className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-all duration-300">
                    THE MENU
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavigationMenuLink asChild className="bg-transparent hover:bg-transparent focus:bg-transparent data-[active]:bg-transparent data-[state=open]:bg-transparent">
                  <Link to="/TheKitchenPage" className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-all duration-300">
                    THE KITCHEN
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {}
        <div className="hidden md:flex flex-shrink-0">
          <Button asChild variant="default" className="bg-transparent text-primary border border-primary hover:bg-primary hover:text-primary-foreground font-mono text-[10px] uppercase tracking-[0.2em] px-8 rounded-none transition-all duration-500">
            <Link to="#contact">RESERVE</Link>
          </Button>
        </div>

        {}
        <div className="md:hidden flex items-center">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-accent hover:text-accent-foreground" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6 stroke-[1]" /> : <Menu className="h-6 w-6 stroke-[1]" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-x-0 top-20 bottom-0 z-40 bg-background/95 backdrop-blur-xl border-t border-border md:hidden flex flex-col p-6 animate-in fade-in slide-in-from-top-5 duration-300 overflow-y-auto">
          <div className="flex flex-col items-center gap-12 mt-12 w-full max-w-md mx-auto">
            <MenuProvider menu_id="46" className="w-full flex justify-center">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            
            <div className="w-full pt-8 border-t border-border/50">
              <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none h-14 text-[10px] font-mono uppercase tracking-[0.2em]">
                <Link to="#contact" onClick={() => setIsMobileMenuOpen(false)}>
                  RESERVE A TABLE
                </Link>
              </Button>
            </div>
          </div>
        </div>}
    </header>;
}